import { useEffect } from "react";

/* ------------------------------------------------------------------
   Replaces the AOS (Animate On Scroll) library with a ~30-line
   IntersectionObserver hook. Same author-facing API as before:

     <div data-reveal="fade-up" data-reveal-delay="100">...</div>

   instead of:

     <div data-aos="fade-up" data-aos-delay="100">...</div>

   Pairs with src/styles/reveal.css, which defines the actual CSS
   transitions. Animations are CSS-only; this hook just toggles a
   class once an element enters the viewport, and disconnects each
   element after its first reveal (mirrors AOS's "once: true").

   Respects prefers-reduced-motion by revealing everything immediately.
------------------------------------------------------------------ */
export default function useScrollReveal(deps = []) {
    useEffect(() => {
        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const nodes = document.querySelectorAll("[data-reveal]");

        if (reduceMotion || typeof IntersectionObserver === "undefined") {
            nodes.forEach((el) => el.classList.add("is-revealed"));
            return;
        }

        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("is-revealed");
                        io.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
        );

        nodes.forEach((el) => io.observe(el));
        return () => io.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}
